import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/feature/SEOHead";
import { mangaList, seoData, mangaCategories } from "@/mocks/manga";

function MangaCard({ manga }: { manga: typeof mangaList[0] }) {
  return (
    <Link
      to={`/manga/${manga.slug}`}
      className="group block"
    >
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
        <img
          src={manga.coverImage}
          alt={manga.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <span className="text-background-50 text-sm font-medium">
            {manga.status === "已完结" ? "已完结 · 共" + manga.chapters[0].chapterNumber + "话" : "连载中"}
          </span>
        </div>
        {manga.status === "已完结" && (
          <span className="absolute top-2 right-2 bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded-full font-medium">
            完结
          </span>
        )}
      </div>
      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
        {manga.title}
      </h3>
      <div className="flex items-center gap-2 text-xs text-foreground-500">
        <span>{manga.author}</span>
        <span>·</span>
        <span className="flex items-center gap-0.5">
          <i className="ri-star-fill text-accent-500 text-xs"></i>
          {manga.rating}
        </span>
      </div>
    </Link>
  );
}

function RankCard({ manga, rank }: { manga: typeof mangaList[0]; rank: number }) {
  return (
    <Link
      to={`/manga/${manga.slug}`}
      className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors group"
    >
      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
        rank === 1 ? "bg-accent-500 text-background-50" :
        rank === 2 ? "bg-primary-400 text-background-50" :
        rank === 3 ? "bg-secondary-500 text-background-50" :
        "bg-background-200 text-foreground-600"
      }`}>
        {rank}
      </span>
      <div className="w-10 h-14 flex-shrink-0 rounded overflow-hidden">
        <img src={manga.coverImage} alt={manga.title} className="w-full h-full object-cover object-top" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
          {manga.title}
        </h4>
        <div className="flex items-center gap-2 text-xs text-foreground-500 mt-0.5">
          <span className="flex items-center gap-0.5">
            <i className="ri-star-fill text-accent-500 text-xs"></i>
            {manga.rating}
          </span>
          <span>{(manga.views / 10000).toFixed(1)}万阅</span>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const hotManga = mangaList.filter((m) => m.category === "romance" || m.rating >= 9.4).slice(0, 8);
  const latestManga = [...mangaList].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()).slice(0, 8);
  const rankedManga = [...mangaList].sort((a, b) => b.views - a.views).slice(0, 10);

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": seoData.siteName,
    "url": seoData.url,
    "description": seoData.description,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${seoData.url}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl={seoData.url}
        ogImage="https://readdy.ai/api/search-image?query=Chinese%20manga%20website%20banner%2C%20colorful%20romance%20manga%20illustration%20with%20cherry%20blossoms%2C%20warm%20pink%20and%20golden%20tones%2C%20attractive%20design%2C%20clean%20composition&width=1200&height=630&seq=home-og-image&orientation=landscape"
        schemaJson={homeSchema}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=stylized%20warm%20toned%20abstract%20illustration%20with%20soft%20gradient%20from%20coral%20pink%20to%20golden%20amber%2C%20dreamy%20atmospheric%20composition%2C%20subtle%20cherry%20blossom%20petals%20floating%2C%20clean%20minimal%20design%2C%20artistic%20digital%20painting%20style%2C%20no%20text%2C%20abstract%20background&width=1600&height=700&seq=home-hero-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/30 to-foreground-900/60"></div>

          <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
            <p className="text-background-50/80 text-sm md:text-base mb-3 animate-fade-in-up tracking-wider">
              36漫画网 · 海量正版漫画免费阅读
            </p>
            <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-4 animate-fade-in-up animate-delay-100 leading-tight">
              3077甜蜜暴击第二季
            </h1>
            <p className="text-background-50/70 text-sm md:text-lg mb-8 animate-fade-in-up animate-delay-200 max-w-xl mx-auto leading-relaxed">
              全集无删减 · 在线免费阅读 · 所有章节一网打尽 · 享受原汁原味的完整故事
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 animate-fade-in-up animate-delay-300">
              <Link
                to="/manga/3077-sweet-attack-season2"
                className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full font-semibold text-sm transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-play-circle-line mr-2"></i>
                立即阅读
              </Link>
              <Link
                to="/category/hot"
                className="bg-background-50/15 hover:bg-background-50/25 text-background-50 border border-background-50/30 px-8 py-3 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-fire-line mr-2"></i>
                热门推荐
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 mt-10 animate-fade-in-up animate-delay-400">
              <div className="text-center">
                <p className="text-background-50 text-2xl md:text-3xl font-bold">10万+</p>
                <p className="text-background-50/60 text-xs mt-1">漫画作品</p>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <p className="text-background-50 text-2xl md:text-3xl font-bold">500万+</p>
                <p className="text-background-50/60 text-xs mt-1">日活用户</p>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <p className="text-background-50 text-2xl md:text-3xl font-bold">99.9%</p>
                <p className="text-background-50/60 text-xs mt-1">好评率</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Quick Nav */}
        <section className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-4">
            <div className="flex items-center gap-2 overflow-x-auto flex-wrap">
              {mangaCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors bg-background-50 text-foreground-600 hover:text-primary-500 hover:bg-primary-50 border border-background-200/70"
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Manga Section */}
        <section className="w-full py-14 md:py-18 px-4 md:px-6">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">
                  <i className="ri-fire-line text-primary-500 mr-2"></i>
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">大家都在看的精彩漫画</p>
              </div>
              <Link
                to="/category/hot"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
              >
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {hotManga.map((manga) => (
                <MangaCard key={manga.id} manga={manga} />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Banner */}
        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-[1280px] mx-auto">
            <Link
              to="/manga/3077-sweet-attack-season2"
              className="block relative overflow-hidden rounded-2xl h-[280px] md:h-[360px] group"
            >
              <img
                src="https://readdy.ai/api/search-image?query=warm%20romantic%20illustration%20of%20young%20couple%20in%20school%20uniform%20under%20cherry%20blossom%20tree%2C%20soft%20pink%20and%20golden%20sunset%20light%2C%20dreamy%20atmosphere%2C%20anime%20manga%20style%2C%20wide%20banner%20composition%2C%20clean%20background%20with%20subtle%20petals&width=1400&height=400&seq=home-featured-banner&orientation=landscape"
                alt="3077甜蜜暴击第二季"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-foreground-900/70 via-foreground-900/30 to-transparent"></div>
              <div className="absolute left-8 md:left-14 top-1/2 -translate-y-1/2 max-w-md">
                <span className="inline-block bg-accent-500 text-background-50 text-xs px-3 py-1 rounded-full mb-3 font-medium">
                  重磅推荐
                </span>
                <h2 className="text-2xl md:text-4xl font-bold text-background-50 mb-3 leading-tight">
                  3077甜蜜暴击 第二季
                </h2>
                <p className="text-background-50/70 text-sm md:text-base mb-4 line-clamp-2">
                  甜蜜暴击第二季重磅回归！林小糖和陆辰风的故事继续上演，新的对手、新的考验，他们的感情将何去何从？
                </p>
                <span className="inline-flex items-center gap-1 text-sm text-background-50 font-medium whitespace-nowrap">
                  立即阅读 <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </span>
              </div>
            </Link>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="w-full py-14 md:py-18 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">
                  <i className="ri-sun-line text-accent-500 mr-2"></i>
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500 mt-1">每日新鲜章节速递</p>
              </div>
              <Link
                to="/category/new"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
              >
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {latestManga.map((manga) => (
                <MangaCard key={manga.id} manga={manga} />
              ))}
            </div>
          </div>
        </section>

        {/* Ranking + New Chapters */}
        <section className="w-full py-14 md:py-18 px-4 md:px-6">
          <div className="max-w-[1280px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
                    <i className="ri-trophy-line text-accent-500 mr-2"></i>
                    人气排行
                  </h2>
                </div>
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-4 space-y-1">
                  {rankedManga.map((manga, idx) => (
                    <RankCard key={manga.id} manga={manga} rank={idx + 1} />
                  ))}
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
                    <i className="ri-bookmark-line text-secondary-500 mr-2"></i>
                    完结佳作
                  </h2>
                  <Link
                    to="/category/completed"
                    className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
                  >
                    查看全部 <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
                  {mangaList.filter((m) => m.status === "已完结").slice(0, 3).map((manga) => (
                    <MangaCard key={manga.id} manga={manga} />
                  ))}
                </div>

                <div className="mt-8 bg-background-50 rounded-xl border border-background-200/70 p-5">
                  <h3 className="text-base font-semibold text-foreground-800 mb-4">阅读提示</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-smartphone-line text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">多端同步</p>
                        <p className="text-xs text-foreground-500 mt-0.5">PC、手机、平板无缝切换阅读</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                        <i className="ri-hd-line text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">高清画质</p>
                        <p className="text-xs text-foreground-500 mt-0.5">全站漫画高清呈现，细节不丢失</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0">
                        <i className="ri-shield-check-line text-secondary-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">正版授权</p>
                        <p className="text-xs text-foreground-500 mt-0.5">10万+正版漫画，放心阅读</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}